import {
    FETCH_GROUP_INVITE_LINKS_REQUESTED,
    FETCH_GROUP_INVITE_LINKS_SUCCEEDED,
    FETCH_GROUP_INVITE_LINKS_FAILED,
    CREATE_GROUP_INVITE_LINK_REQUESTED,
    CREATE_GROUP_INVITE_LINK_SUCCEEDED,
    CREATE_GROUP_INVITE_LINK_FAILED,
    UPDATE_GROUP_INVITE_LINK_REQUESTED,
    UPDATE_GROUP_INVITE_LINK_SUCCEEDED,
    UPDATE_GROUP_INVITE_LINK_FAILED,
    DELETE_GROUP_INVITE_LINK_REQUESTED,
    DELETE_GROUP_INVITE_LINK_SUCCEEDED,
    DELETE_GROUP_INVITE_LINK_FAILED,
    SHOW_GROUP_INVITE_DIALOG,
    HIDE_GROUP_INVITE_DIALOG
} from 'shared_redux/actions/group_invite_links';

const initialState = {
    inviteLinks: [],
    currentInviteLink: null,
    isLoading: false,
    isShowingDialog: false,
    currentGroupId: null,
    error: null
};

export default function groupInviteLinksReducer(state = initialState, action) {
    switch (action.type) {
        case FETCH_GROUP_INVITE_LINKS_REQUESTED:
            return {
                ...state,
                isLoading: true,
                error: null
            };
        
        case FETCH_GROUP_INVITE_LINKS_SUCCEEDED:
            // Extract inviteLinks and groupId from the payload
            const payload = action.payload || {};
            const inviteLinks = Array.isArray(payload.inviteLinks) ? payload.inviteLinks : [];
            const groupId = payload.groupId;

            console.log('[Reducer] FETCH_GROUP_INVITE_LINKS_SUCCEEDED with:', {
                inviteLinks: inviteLinks.length,
                groupId
            });
            
            // Find active invite link for this group if it exists
            const activeGroupLink = groupId ? 
                inviteLinks.find(link => link.group_id === groupId && link.is_active) : null;
            
            console.log('[Reducer] Found active link for group:', activeGroupLink ? 'yes' : 'no');
            
            return {
                ...state,
                inviteLinks,
                // Update the current group ID
                currentGroupId: groupId || state.currentGroupId,
                // Set the current invite link to the active one for this group
                currentInviteLink: activeGroupLink || (inviteLinks.length > 0 ? inviteLinks[0] : null),
                isLoading: false
            };
        
        case FETCH_GROUP_INVITE_LINKS_FAILED:
            return {
                ...state,
                inviteLinks: [],
                currentInviteLink: null,
                isLoading: false,
                error: 'Failed to fetch group invite links'
            };
        
        case CREATE_GROUP_INVITE_LINK_REQUESTED:
            return {
                ...state,
                isLoading: true,
                error: null
            };
        
        case CREATE_GROUP_INVITE_LINK_SUCCEEDED:
            // Ensure we have a valid payload
            if (!action.payload) {
                return {
                    ...state,
                    isLoading: false
                };
            }
            
            // Check if we're updating an existing link or creating a new one
            const existingLinkIndex = state.inviteLinks.findIndex(link => 
                link.id === action.payload.id || link.group_id === action.payload.group_id
            );
            
            // Create a new array of invite links
            let updatedLinks;
            if (existingLinkIndex >= 0) {
                // Replace the existing link
                updatedLinks = [...state.inviteLinks];
                updatedLinks[existingLinkIndex] = action.payload;
            } else {
                // Add the new link
                updatedLinks = [action.payload, ...state.inviteLinks];
            }
            
            return {
                ...state,
                inviteLinks: updatedLinks,
                currentInviteLink: action.payload,
                isShowingDialog: false,
                isLoading: false,
                error: null
            };
        
        case CREATE_GROUP_INVITE_LINK_FAILED:
            return {
                ...state,
                isLoading: false,
                error: 'Failed to create group invite link'
            };
        
        case UPDATE_GROUP_INVITE_LINK_REQUESTED:
            return {
                ...state,
                isLoading: true,
                error: null
            };
        
        case UPDATE_GROUP_INVITE_LINK_SUCCEEDED:
            return {
                ...state,
                inviteLinks: state.inviteLinks.map(link => 
                    link.id === action.payload.id ? action.payload : link
                ),
                currentInviteLink: action.payload,
                isLoading: false
            };
        
        case UPDATE_GROUP_INVITE_LINK_FAILED:
            return {
                ...state,
                isLoading: false,
                error: 'Failed to update group invite link'
            };
        
        case DELETE_GROUP_INVITE_LINK_REQUESTED:
            return {
                ...state,
                isLoading: true,
                error: null
            };
        
        case DELETE_GROUP_INVITE_LINK_SUCCEEDED:
            // Get the ID from the payload object
            const deletedLinkId = action.payload.id;
            console.log('[Reducer] Deactivating link with ID:', deletedLinkId);
            
            return {
                ...state,
                // Remove the deactivated link from the inviteLinks array
                inviteLinks: state.inviteLinks.filter(link => link.id !== deletedLinkId),
                // Reset the current invite link
                currentInviteLink: null,
                isLoading: false,
                // Keep the dialog open so the user can create a new link
                isShowingDialog: true
            };
        
        case DELETE_GROUP_INVITE_LINK_FAILED:
            return {
                ...state,
                isLoading: false,
                error: 'Failed to delete group invite link'
            };
        
        case SHOW_GROUP_INVITE_DIALOG:
            // First, let's log what we received for debugging
            console.log('[Reducer] SHOW_GROUP_INVITE_DIALOG payload:', action.payload);
            
            // Get data from the payload
            const teamId = action.payload?.teamId || null;
            const teamName = action.payload?.teamName || null;
            const requestedGroupId = action.payload?.groupId || null;
            const groupName = action.payload?.groupName || null;
            
            // Start with the requested group ID or fallback to current state
            let effectiveGroupId = requestedGroupId || state.currentGroupId || null;
            
            console.log(`[Reducer] Initial state - teamId: ${teamId}, requestedGroupId: ${requestedGroupId}, effectiveGroupId: ${effectiveGroupId}`);
            
            // IMPORTANT: This is the key part - if we were provided a groupId from the InviteButtonWithCopy,
            // we should trust it, as it's already done the work to get the team's default group
            if (requestedGroupId) {
                console.log(`[Reducer] Using provided group ID: ${requestedGroupId}`);
                effectiveGroupId = requestedGroupId;
            }
            // If we didn't get a groupId but did get a teamId, we need to look up the team's default group
            else if (teamId) {
                console.log(`[Reducer] Need to find default group for team: ${teamId}`);
                
                // Look for the team in the window's Redux state if available
                if (typeof window !== 'undefined' && window.__REDUX_STATE__) {
                    const reduxState = window.__REDUX_STATE__;
                    
                    if (reduxState.database?.Teams?.items?.[teamId]) {
                        const team = reduxState.database.Teams.items[teamId];
                        
                        if (team.default_group_id) {
                            console.log(`[Reducer] Found team's default_group_id: ${team.default_group_id}`);
                            effectiveGroupId = team.default_group_id;
                        } else {
                            console.log(`[Reducer] Team found but no default_group_id available`);
                        }
                    } else {
                        console.log(`[Reducer] Could not find team in Redux state`);  
                    }
                }
            }
            
            // Check if this is a different group than before
            const isNewGroup = effectiveGroupId !== state.currentGroupId;
            
            console.log('[Reducer] Final decision:', { 
                teamId, 
                requestedGroupId, 
                effectiveGroupId, 
                isNewGroup 
            });
            
            // Do NOT modify the URL when opening the dialog
            // Instead, just keep the group ID in the Redux state
            
            return {
                ...state,
                isShowingDialog: true,
                currentGroupId: effectiveGroupId,
                // Store the teamId and team name for use in the dialog
                currentTeamId: teamId,
                currentTeamName: teamName,
                // Store the group name for use in the dialog
                currentGroupName: groupName,
                // Reset current invite link if we're showing a different group
                currentInviteLink: isNewGroup ? null : state.currentInviteLink
            };
        
        case HIDE_GROUP_INVITE_DIALOG:
            return {
                ...state,
                isShowingDialog: false
            };
        
        default:
            return state;
    }
}
